import store from "../store/store";
import * as TaskActions from "../store/actions/taskActions";
import newHRTimestamp from "../lib/picos/timestamp";
import {getCurrentUserId} from "../lib/picos/currentLogin";
import * as TaskHelper from "./TaskHelper";
import {AWAITING_TITLE, getBillableRate, getRelatedMatters} from "./TaskHelper";
import {uuid} from "../@types/schema";
import Task, {AssignedToType} from "../entities/task";
import {RestoreStatus, RestoreStatusType} from "../store/actions/miscActions";
import {canClaimTaskForTimerWork, canStartTask, hasTaskTimerStarted} from './taskTimerState';
import type {SaveIntent} from '../lib/taskLineage';
import {showTimerCapNotice} from './timerCapNotice';

/**
 * OWA-7 stale-draft guard: is the live (Redux) task newer than a recovered
 * autosave draft of the same task?
 *
 * Crash-recovery re-opens cached task drafts on mount. A draft captured with
 * running=true can outlive the live task being stopped/edited (e.g. overnight),
 * and re-opening + saving it would resurrect the timer. We compare the server
 * timestamp `dbAddedOn` first (the canonical "live is newer" signal, same idiom
 * as InvoiceCard/ContactCard), falling back to `addedOn` when `dbAddedOn` is not
 * yet populated on one side. Returns true ONLY when the live task is strictly
 * newer than the draft — a draft that is newer-or-equal must still be recovered.
 *
 * @param {Task} live - the live task from Redux/props
 * @param {Task} draft - the recovered autosave draft
 * @returns {boolean} true if the live task is strictly newer than the draft
 */
export const isLiveTaskNewer = (live: Task, draft: Task): boolean => {
    if (!live || !draft) return false;

    const liveDb = live.dbAddedOn ?? null;
    const draftDb = draft.dbAddedOn ?? null;
    if (liveDb != null && draftDb != null) return liveDb > draftDb;

    // Fall back to addedOn when dbAddedOn is missing on either side.
    const liveAdded = live.addedOn ?? 0;
    const draftAdded = draft.addedOn ?? 0;
    return liveAdded > draftAdded;
};

/**
 * OWA-7 stale-draft recovery guard. Given the live-task index (props.tasks) and
 * a recovered autosave draft, returns true when the draft is STALE and must NOT
 * be re-opened — i.e. a live task with the same id exists AND is strictly newer
 * than the draft. Used identically by the crash-recovery effects in TaskGrid and
 * AssociatedTasks. A draft with no live counterpart, or one that is
 * newer-or-equal to the live task, is NOT stale (it is still recovered).
 *
 * @param {{get: (id: string) => Task | undefined} | null | undefined} liveTasks - the live-task index (props.tasks)
 * @param {Task} draft - the recovered autosave draft
 * @returns {boolean} true if the draft is stale and should be skipped
 */
export const isStaleDraft = (
    liveTasks: {get: (id: string) => Task | null | undefined} | null | undefined,
    draft: Task,
): boolean => {
    const live = liveTasks?.get(draft.id);
    return !!live && isLiveTaskNewer(live, draft);
};

/**
 * OWA-525 crash-recovery gate: is the IndexedDB backup restore complete?
 *
 * Draft crash-recovery (TaskGrid / AssociatedTasks) leans on isStaleDraft to
 * reject a stale running draft — but isStaleDraft can only do that once the
 * LIVE task is present in the index it is handed. That index is populated by
 * `restore._loadBackups`, which dispatches `setRestoreStatus(Restored)` only
 * AFTER every entity (tasks included) has been loaded into Redux. If recovery
 * runs before then, the index is empty, isStaleDraft returns false (no live
 * counterpart → "not stale, recover it"), and a stale running draft is
 * re-opened; saving it re-runs stopTimer against a stale lastStart, inflating
 * elapsedTime and clearing completionDate. So both components must defer
 * recovery until this returns true, at which point isStaleDraft is meaningful.
 *
 * @param {RestoreStatusType} restoreStatus - misc.restoreStatus from Redux
 * @returns {boolean} true once backups have been restored into Redux
 */
export const isRestoreComplete = (restoreStatus: RestoreStatusType): boolean =>
    restoreStatus === RestoreStatus.Restored;

/**
 * Start up the task timer.  Start by stopping any other timers for the user.
 *
 * @param {Task} task - the task to start
 * @param {uuid} [userId] - optional id of the user that started the task
 * @return {Task}
 */
export const startTimer = (task: Task, userId?: uuid): Task => {
    if (task.status === 'D') {
        console.warn('startTimer: Ignoring deleted task', task.id);
        return task;
    }
    if (!userId) userId = getCurrentUserId();

    const taskClone = task.clone();
    const tasks = store.getState().tasks;

    const timerPreviouslyStarted = hasTaskTimerStarted(taskClone);
    // OWA-842: the saved task's lock counts too; the copy being started can be older than it
    const saved: Task | undefined = tasks?.get?.(taskClone.id);
    const assigneeLocked = timerPreviouslyStarted || (!!saved && hasTaskTimerStarted(saved));
    if (!canStartTask({...taskClone, timerStarted:assigneeLocked}, userId)) return task;
    // OWA-1648: the one start rule. Invoiced and completed tasks, and tasks this user may not run, do not start.
    if (!TaskHelper.canPlay(taskClone, userId)) return task;

    // Stop all existing timers: check for running timers started BY THIS USER.
    // Stop them and commit their times.
    tasks.forEach(t => {
        // Not this task, started by this user, and is running
        if (t.running &&
            (t.id !== taskClone.id) &&
            (t.assignedTo === userId || (!t.assignedTo && t.addedBy === userId))
        ) {
            // Stop the timer and record the values. OWA-998: a reconciling save; refused as
            // stale it is dropped, and the clean-up after the next fetch works from the newer row
            stopTimer(t, userId, false, 'reconcile');
        }
    });

    // Start up this task

    // If this task has not yet been assigned to a user (but to a group), collapse the field to the current user
    if (!assigneeLocked && canClaimTaskForTimerWork(taskClone)) {
        taskClone.assignedTo = userId;
        taskClone.assignedToType = AssignedToType.User;
    }

    // Get the now current billing rate and set it for laborRate
    taskClone.running = true;
    taskClone.lastStart = Date.now();
    taskClone.addedBy = userId;
    taskClone.addedOn = newHRTimestamp();

    // Set the billable rate and start time if this is the first time added
    if (!timerPreviouslyStarted) {
        taskClone.timerStarted = true;

        // Start time is the first time this task was ever started (a Date). lastStart is the time in millis the
        // task was last started.
        if (taskClone.elapsedTime === 0) {
            taskClone.startTime = new Date();
            taskClone.laborRate = getBillableRate(taskClone.assignedTo, getRelatedMatters(taskClone)[0]);
        }
    }

    // Always need a title
    if (!taskClone.title) taskClone.title = AWAITING_TITLE;
    store.dispatch(TaskActions.saveTaskQueue(taskClone));

    // Send back the clone
    return taskClone;
};

/** A stop delta of this much or more is not counted (OWA-525). */
export const MAX_TIMER_DELTA = 24 * 60 * 60 * 1000;

/**
 * OWA-525: how much a stop adds to elapsed time. The delta is this device's clock minus
 * `lastStart`, which the starting device's clock wrote. A negative delta (this clock is behind)
 * or one of 24 hours or more cannot be trusted: the stop adds nothing and the user is asked for
 * the time worked. Before OWA-525 such a stop added a flat 24 hours, or dropped a negative delta
 * without saying so.
 *
 * @param {number} lastStart - when the timer was started, in milliseconds
 * @param {number} now - this device's clock, in milliseconds
 * @returns {{add: number, askForTime: boolean}}
 */
export const timerStopDelta = (lastStart: number, now: number): {add: number, askForTime: boolean} => {
    const delta = now - lastStart;
    if (delta < 0 || delta >= MAX_TIMER_DELTA) return {add: 0, askForTime: true};
    return {add: delta, askForTime: false};
};

/**
 * Stop a running timer. A stop whose delta cannot be trusted (see timerStopDelta) still stops
 * the timer, adds nothing, and shows the notice asking for the time worked.
 *
 * @param {Task} task - the task stop
 * @param {uuid} [userId] - optional id of the user that stopped the task
 * @param {boolean} noSave
 * @param {SaveIntent} [intent] - 'reconcile' for automatic clean-up (OWA-998)
 * @returns {Task}
 */
export const stopTimer = (task: Task, userId?: uuid, noSave: boolean = false, intent?: SaveIntent): Task => {
    if (task.status === 'D') {
        console.warn('stopTimer: Ignoring deleted task', task.id);
        return task;
    }
    if (!userId) userId = getCurrentUserId();

    const taskClone = task.clone();
    let timeNotAdded = false;

    // Stop the task if it is running
    if (taskClone.running) {
        // Make sure we are not adding to zero, or we get a ridiculous time in the timer.
        if (!taskClone.lastStart) {
            console.warn('stopTimer: Stopping a timer with no lastStart.', task.id);
        }
        else {
            const now = Date.now();
            const {add, askForTime} = timerStopDelta(taskClone.lastStart, now);
            if (askForTime) {
                // OWA-525: the delta cannot be trusted, so nothing is added and the user is asked
                console.warn(`stopTimer: no time added. Task ${task.id}, delta=${now - taskClone.lastStart}ms (${((now - taskClone.lastStart) / 3600000).toFixed(1)}h).`);
                timeNotAdded = true;
            }
            else taskClone.elapsedTime = (taskClone.elapsedTime || 0) + add;
            console.debug(`TIMER_STOP: task=${task.id}, prev=${task.elapsedTime}, delta=${now - taskClone.lastStart}ms, new=${taskClone.elapsedTime}, running=${task.running}, lastStart=${task.lastStart}`);
        }
    }

    taskClone.lastStart = 0.0;
    taskClone.running = false;
    taskClone.addedBy = userId;
    taskClone.addedOn = newHRTimestamp();

    // Always need a title
    if (!task.title) taskClone.title = AWAITING_TITLE;

    // Recompute cost
    const updatedTask = TaskHelper.updateCost(taskClone);

    // Save the task
    if (!noSave) store.dispatch(TaskActions.saveTaskQueue(updatedTask, undefined, intent));

    // OWA-525: ask for the time actually worked
    if (timeNotAdded) showTimerCapNotice(updatedTask);

    return updatedTask;
};
